/**
 * CustomSelect Web Component
 * ==========================
 *
 * A reusable, accessible custom select dropdown that wraps a native <select>.
 *
 * Features:
 * - Auto-parses options from the native <select>
 * - Search input when options exceed threshold (default: 10)
 * - Keyboard navigation (arrows, enter, escape)
 * - Syncs selection to native <select> with change events
 * - Supports leading icons via .svg-wrapper--first
 * - Click-outside and escape to close
 *
 * Usage:
 *   <custom-select>
 *     <select name="country" required>
 *       <option value="" disabled selected>Choose country</option>
 *       <option value="us">United States</option>
 *     </select>
 *     <span class="svg-wrapper svg-wrapper--first"><!-- icon --></span>
 *   </custom-select>
 *
 * Attributes:
 *   data-search-threshold  – Options count to enable search (default: 10)
 *   data-search-placeholder – Placeholder for search input (default: "Search...")
 */

if (!customElements.get("custom-select")) {
  /** Chevron SVG shared across all instances */
  const CHEVRON_SVG = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  class CustomSelect extends HTMLElement {
    /* ------- Private Fields ------- */

    /** @type {HTMLSelectElement | null} */
    #select = null;

    /** @type {HTMLElement | null} */
    #trigger = null;

    /** @type {HTMLElement | null} */
    #dropdown = null;

    /** @type {HTMLElement | null} */
    #optionsList = null;

    /** @type {HTMLInputElement | null} */
    #searchInput = null;

    /** @type {boolean} */
    #isOpen = false;

    /** @type {number} Visually focused option index (-1 = none) */
    #focusedIdx = -1;

    /** @type {number} */
    #searchThreshold = 10;

    /** @type {Array<{value:string, text:string, disabled:boolean, selected:boolean, placeholder:boolean}>} */
    #opts = [];

    /** @type {MutationObserver | null} */
    #observer = null;

    /** @type {boolean} */
    #isBuilt = false;

    /** @type {number | null} */
    #searchTimeout = null;

    /* ------- Lifecycle ------- */

    connectedCallback() {
      this.#init();

      // Setup MutationObserver to handle DOM changes (like morphing or delayed parsing)
      if (!this.#observer) {
        this.#observer = new MutationObserver(() => {
          this.#init();
        });
        this.#observer.observe(this, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      if (this.#observer) {
        this.#observer.disconnect();
      }
      this.#destroy();
    }

    /* ------- Init / Parse ------- */

    #init() {
      this.#observer?.disconnect();
      
      this.#select = this.querySelector("select");
      if (!this.#select) {
        // Not parsed yet, observer will catch it when it arrives
        this.#observer?.observe(this, { childList: true, subtree: true });
        return;
      }

      this.#searchThreshold =
        parseInt(this.dataset.searchThreshold || "10", 10) || 10;

      this.#parseOptions();

      // Ensure we only build the UI once
      if (!this.#isBuilt) {
        // Hide native select from interactions (CSS handles visual hiding via .is-ready)
        this.#select.setAttribute("tabindex", "-1");
        this.#select.setAttribute("aria-hidden", "true");

        this.#buildUI();
        this.#isBuilt = true;
      } else {
        // If morphing triggered observer, just refresh options/display
        this.#renderOptions();
      }
      
      this.#attachEvents();
      this.#updateDisplay();

      // Mark as initialized
      this.classList.add("is-ready");
      
      this.#observer?.observe(this, { childList: true, subtree: true });
    }

    /** Parse native <option> elements into a structured array. */
    #parseOptions() {
      if (!this.#select) return;
      this.#opts = Array.from(this.#select.options).map((o) => ({
        value: o.value,
        text: (o.textContent || "").trim(),
        disabled: o.disabled,
        selected: o.selected,
        placeholder: o.disabled && o.value === "",
      }));
    }

    /* ------- UI Construction ------- */

    #buildUI() {
      const icon = this.querySelector(".svg-wrapper--first");

      // --- Trigger ---
      this.#trigger = document.createElement("div");
      this.#trigger.className = "custom-select__trigger";
      this.#trigger.setAttribute("role", "combobox");
      this.#trigger.setAttribute("aria-expanded", "false");
      this.#trigger.setAttribute("aria-haspopup", "listbox");
      this.#trigger.setAttribute("tabindex", "0");

      if (icon) this.#trigger.appendChild(icon);

      const valEl = document.createElement("span");
      valEl.className = "custom-select__value";
      this.#trigger.appendChild(valEl);

      const arrow = document.createElement("span");
      arrow.className = "custom-select__arrow";
      arrow.innerHTML = CHEVRON_SVG;
      this.#trigger.appendChild(arrow);

      // --- Dropdown ---
      this.#dropdown = document.createElement("div");
      this.#dropdown.className = "custom-select__dropdown";

      // Search (if above threshold)
      const selectable = this.#opts.filter((o) => !o.placeholder);
      if (selectable.length > this.#searchThreshold) {
        const wrap = document.createElement("div");
        wrap.className = "custom-select__search-wrapper";

        this.#searchInput = document.createElement("input");
        this.#searchInput.type = "text";
        this.#searchInput.className = "custom-select__search";
        this.#searchInput.placeholder =
          this.dataset.searchPlaceholder || "Search...";
        this.#searchInput.setAttribute("autocomplete", "off");
        this.#searchInput.setAttribute("aria-label", "Search options");

        wrap.appendChild(this.#searchInput);
        this.#dropdown.appendChild(wrap);
      }

      // Options list
      this.#optionsList = document.createElement("div");
      this.#optionsList.className = "custom-select__options";
      this.#optionsList.setAttribute("role", "listbox");

      this.#renderOptions();
      this.#dropdown.appendChild(this.#optionsList);

      this.appendChild(this.#trigger);
      this.appendChild(this.#dropdown);
    }

    /**
     * Render visible options, optionally filtered.
     * @param {string} [filter=""]
     */
    #renderOptions(filter = "") {
      if (!this.#optionsList) return;

      // Disconnect observer while modifying component's own DOM
      this.#observer?.disconnect();

      this.#optionsList.innerHTML = "";
      this.#focusedIdx = -1;

      const lc = filter.toLowerCase();
      let count = 0;

      this.#opts.forEach((o, i) => {
        if (o.placeholder) return;
        if (filter && !o.text.toLowerCase().includes(lc)) return;

        const el = document.createElement("div");
        el.className = "custom-select__option";
        el.setAttribute("role", "option");
        el.setAttribute("data-value", o.value);
        el.setAttribute("data-index", String(i));
        el.setAttribute("aria-selected", String(o.selected));

        if (o.disabled) el.classList.add("is-disabled");
        if (o.selected && !o.placeholder) el.classList.add("is-selected");

        el.textContent = o.text;
        if (this.#optionsList) {
          this.#optionsList.appendChild(el);
          count++;
        }
      });

      if (count === 0 && this.#optionsList) {
        const empty = document.createElement("div");
        empty.className = "custom-select__empty";
        empty.textContent = "No results found";
        this.#optionsList.appendChild(empty);
      }

      // Re-observe after DOM mutations are done
      this.#observer?.observe(this, { childList: true, subtree: true });
    }

    /* ------- Events ------- */

    #attachEvents() {
      this.#trigger?.addEventListener("click", this.#onTriggerClick);
      this.#trigger?.addEventListener("keydown", this.#onKeydown);
      this.#optionsList?.addEventListener("click", this.#onOptionClick);

      if (this.#searchInput) {
        this.#searchInput.addEventListener("input", this.#onSearch);
        this.#searchInput.addEventListener("keydown", this.#onKeydown);
      }

      document.addEventListener("click", this.#onDocClick);
      document.addEventListener("custom-select:open", this.#onOtherOpen);
      document.addEventListener("custom-calendar:open", this.#onOtherOpen);
    }

    /** @param {MouseEvent} e */
    #onTriggerClick = (e) => {
      e.stopPropagation();
      this.#isOpen ? this.#close() : this.#open();
    };

    /** @param {MouseEvent} e */
    #onOptionClick = (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      const el = target.closest(".custom-select__option");
      if (!el || el.classList.contains("is-disabled")) return;
      e.stopPropagation();

      const idx = parseInt(el.getAttribute("data-index") || "", 10);
      if (!isNaN(idx)) this.#selectOption(idx);
    };

    /** @param {Event} e */
    #onSearch = (e) => {
      const val = /** @type {HTMLInputElement} */ (e.target).value;
      
      // Debounce search input
      if (this.#searchTimeout) {
        clearTimeout(this.#searchTimeout);
      }
      this.#searchTimeout = window.setTimeout(() => {
        this.#renderOptions(val);
      }, 300);
    };

    /**
     * Unified keyboard handler for trigger and search input.
     * @param {KeyboardEvent} e
     */
    #onKeydown = (e) => {
      switch (e.key) {
        case "Enter":
        case " ":
          // Space should type in search input, not toggle
          if (e.key === " " && e.target === this.#searchInput) return;
          e.preventDefault();
          if (!this.#isOpen) {
            this.#open();
          } else if (this.#focusedIdx >= 0) {
            this.#selectFocused();
          }
          break;

        case "ArrowDown":
          e.preventDefault();
          if (!this.#isOpen) this.#open();
          this.#moveFocus(1);
          break;

        case "ArrowUp":
          e.preventDefault();
          if (!this.#isOpen) this.#open();
          this.#moveFocus(-1);
          break;

        case "Escape":
          if (this.#isOpen) {
            e.preventDefault();
            this.#close();
          }
          break;
      }
    };

    /** @param {MouseEvent} e */
    #onDocClick = (e) => {
      if (!this.contains(/** @type {Node} */ (e.target)) && this.#isOpen) {
        this.#close();
      }
    };

    /** @param {CustomEvent} e */
    #onOtherOpen = (e) => {
      if (e.detail?.target !== this && this.#isOpen) this.#close();
    };

    /* ------- Open / Close ------- */

    #open() {
      this.#isOpen = true;
      this.classList.add("is-open");
      this.#trigger?.setAttribute("aria-expanded", "true");

      // Reset search and options
      if (this.#searchInput) {
        this.#searchInput.value = "";
        if (this.#searchTimeout) clearTimeout(this.#searchTimeout);
        this.#renderOptions();
        requestAnimationFrame(() => {
          if (this.#searchInput) this.#searchInput.focus();
        });
      }

      // Notify others to close
      document.dispatchEvent(
        new CustomEvent("custom-select:open", { detail: { target: this } })
      );
      document.dispatchEvent(
        new CustomEvent("custom-calendar:open", { detail: { target: this } })
      );

      // Scroll selected into view
      requestAnimationFrame(() => {
        const sel = this.#optionsList?.querySelector(".is-selected");
        if (sel) sel.scrollIntoView({ block: "nearest" });
      });
    }

    #close() {
      this.#isOpen = false;
      this.classList.remove("is-open");
      this.#trigger?.setAttribute("aria-expanded", "false");
      this.#focusedIdx = -1;

      this.#optionsList
        ?.querySelectorAll(".is-focused")
        .forEach((el) => el.classList.remove("is-focused"));

      this.#trigger?.focus();
    }

    /* ------- Selection & Navigation ------- */

    /**
     * Select option by index in #opts array.
     * @param {number} idx
     */
    #selectOption(idx) {
      const opt = this.#opts[idx];
      if (!opt || opt.disabled || opt.placeholder) return;

      // Update internal state
      this.#opts.forEach((o) => (o.selected = false));
      opt.selected = true;

      // Sync native select
      if (this.#select) {
        this.#select.value = opt.value;
        this.#select.dispatchEvent(new Event("change", { bubbles: true }));
      }

      this.#updateDisplay();
      this.#renderOptions();
      this.#close();
    }

    /** Select the currently focused option. */
    #selectFocused() {
      if (!this.#optionsList) return;
      const items = this.#getVisibleOptions();
      if (this.#focusedIdx >= 0 && this.#focusedIdx < items.length) {
        const idx = parseInt(
          items[this.#focusedIdx].getAttribute("data-index") || "",
          10
        );
        if (!isNaN(idx)) this.#selectOption(idx);
      }
    }

    /**
     * Move visual focus by delta (+1 / -1).
     * @param {number} delta
     */
    #moveFocus(delta) {
      const items = this.#getVisibleOptions();
      if (items.length === 0) return;

      // Remove old focus
      this.#optionsList
        ?.querySelectorAll(".is-focused")
        .forEach((el) => el.classList.remove("is-focused"));

      // Calculate new index
      let next = this.#focusedIdx + delta;
      if (next < 0) next = 0;
      if (next >= items.length) next = items.length - 1;

      this.#focusedIdx = next;
      const target = items[next] || null;
      if (target) {
        target.classList.add("is-focused");
        target.scrollIntoView({ block: "nearest" });
      }
    }

    /**
     * Get all non-disabled visible option elements.
     * @returns {HTMLElement[]}
     */
    #getVisibleOptions() {
      if (!this.#optionsList) return [];
      return /** @type {HTMLElement[]} */ (
        Array.from(
          this.#optionsList.querySelectorAll(
            ".custom-select__option:not(.is-disabled)"
          )
        )
      );
    }

    /* ------- Display ------- */

    /** Update trigger text from current selection. */
    #updateDisplay() {
      const valEl = this.#trigger?.querySelector(".custom-select__value");
      if (!valEl) return;

      this.#observer?.disconnect();

      const sel = this.#opts.find((o) => o.selected && !o.placeholder) || null;
      const ph = this.#opts.find((o) => o.placeholder) || null;

      if (sel && sel.text) {
        valEl.textContent = sel.text;
        this.classList.add("selected");
      } else if (ph && ph.text) {
        valEl.textContent = ph.text;
        this.classList.remove("selected");
      }

      this.#observer?.observe(this, { childList: true, subtree: true });
    }

    /* ------- Cleanup ------- */

    #destroy() {
      this.#trigger?.removeEventListener("click", this.#onTriggerClick);
      this.#trigger?.removeEventListener("keydown", this.#onKeydown);
      this.#optionsList?.removeEventListener("click", this.#onOptionClick);

      if (this.#searchInput) {
        this.#searchInput.removeEventListener("input", this.#onSearch);
        this.#searchInput.removeEventListener("keydown", this.#onKeydown);
      }

      document.removeEventListener("click", this.#onDocClick);
      document.removeEventListener("custom-select:open", this.#onOtherOpen);
      document.removeEventListener("custom-calendar:open", this.#onOtherOpen);
    }
  }

  customElements.define("custom-select", CustomSelect);
}
